<template>
    <tr :class="`${isEditRequested || isAddRequested || isDeleteRequested ? 'edit-row' : ''}`">
        <td :colspan="` ${isEditRequested || isAddRequested ? '2' : '1'}`" class="align-top">
            <div class="form-group mb-0" v-if="isEditRequested || isAddRequested">
                <Diagnosis
                    :selected-diagnosis="selectedDiagnosis"
                    :diagnosis-changed="diagnosisChanged"
                    :application="Number(application)"
                />
            </div>
            <span v-else>
                <EmptyTextHandler :text="diag.code" />
            </span>
        </td>
        <td v-if="!isEditRequested && !isAddRequested">
            <EmptyTextHandler :text="diag.description" />
        </td>
        <td class="align-top">
            <template v-if="isEditRequested || isAddRequested">
                <div class="form-group mb-0" v-if="!diag.isStartDateUnknown">
                    <DatePicker :getDate="handleDateChange">
                        <div class="input-group date datepickerInput">
                            <input
                                name="startDate"
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                :value="formatDateInput(diag.startDate)"
                                autocomplete="off"
                                @change="handleDate"
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-group mb-0" v-else>
                    <DatePicker>
                        <div class="input-group date">
                            <input
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                disabled
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-check custom-checkbox form-check-inline">
                    <input
                        type="checkbox"
                        class="form-check-input au-target"
                        :id="`diag-start-${currentView}-${index}`"
                        v-model="diag.isStartDateUnknown"
                    />
                    <label class="form-check-label" :for="`diag-start-${currentView}-${index}`">
                        <span class="me-1">Unknown</span>
                    </label>
                </div>
            </template>
            <template v-else>
                <span v-if="diag.isStartDateUnknown">Unknown</span>
                <EmptyTextHandler v-else :text="formatDate(diag.startDate)" />
            </template>
        </td>
        <td class="align-top">
            <template v-if="isEditRequested || isAddRequested">
                <div class="form-group mb-0" v-if="!diag.isResolvedDateUnknown">
                    <DatePicker :getDate="handleDateChange">
                        <div class="input-group date datepickerInput">
                            <input
                                name="resolvedDate"
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                :value="formatDateInput(diag.resolvedDate)"
                                autocomplete="off"
                                @change="handleDate"
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <!-- Added this extra div to show disabled so that validation would also be hidden -->
                <div class="form-group mb-0" v-else>
                    <DatePicker>
                        <div class="input-group date">
                            <input
                                type="text"
                                class="form-control"
                                placeholder="MM/DD/YYYY"
                                autocomplete="off"
                                disabled
                            />
                            <div class="input-group-addon input-group-append">
                                <div class="input-group-text">
                                    <i class="fa fa-calendar"></i>
                                </div>
                            </div>
                        </div>
                    </DatePicker>
                </div>
                <div class="form-check custom-checkbox form-check-inline">
                    <input
                        type="checkbox"
                        class="form-check-input au-target"
                        :id="`diag-resolved-${currentView}-${index}`"
                        v-model="diag.isResolvedDateUnknown"
                    />
                    <label class="form-check-label" :for="`diag-resolved-${currentView}-${index}`">
                        <span class="me-1">Unknown</span>
                    </label>
                </div>
            </template>
            <template v-else>
                <span v-if="diag.isResolvedDateUnknown">Unknown</span>
                <EmptyTextHandler v-else :text="formatDate(diag.resolvedDate)" />
            </template>
        </td>
        <template v-if="!isDeleteRequested">
            <td class="text-end" v-if="!isEditRequested && !isAddRequested">
                <button class="btn table-btn btn-outline-info" @click="openEditDiagnosis()">
                    <i class="fas fa-edit"></i>
                </button>
                <button class="btn table-btn btn-outline-danger" @click="openDeleteDiagnosis()">
                    <i class="fas fa-trash-alt"></i>
                </button>
                <div class="btn-group table-btn-group" v-if="!first || !last">
                    <button
                        type="button"
                        class="btn px-1 py-0 btn-outline-primary dropdown-toggle"
                        data-bs-toggle="dropdown"
                        :id="`DiagnosisRow__Actions${diag.order}`"
                    >
                        <i class="fas fa-ellipsis-h"></i>
                    </button>
                    <div class="dropdown-menu" :aria-labelledby="`DiagnosisRow__Action${diag.order}`">
                        <a
                            class="dropdown-item"
                            href="javascript:void(0);"
                            @click="moveDiagnosisUp(index, index - 1)"
                            v-if="!first"
                        >
                            Move up
                        </a>
                        <a
                            class="dropdown-item"
                            href="javascript:void(0);"
                            @click="moveDiagnosisDown(index, index + 1)"
                            v-if="!last"
                        >
                            Move down
                        </a>
                    </div>
                </div>
            </td>
        </template>
        <td :colspan="columns" class="p-2 edit" v-if="isDeleteRequested">
            <div class="d-flex flex-column align-items-center">
                <TablePrompt
                    ok-text="Delete"
                    name="delete"
                    :ok="handleDeleteDiagnosis"
                    :cancel="cancelDeleteRequest"
                    :message="deleteMessage"
                    cancel-text="No"
                    :include-yes="false"
                />
            </div>
        </td>
        <td class="button-cell text-end" v-if="isEditRequested">
            <button type="button" class="btn table-btn btn-outline-success" @click="saveEditDiagnosis()">Add</button>
            <button type="button" class="btn table-btn btn-outline-secondary" @click="cancelEditDiagnosis()">
                Cancel
            </button>
        </td>
        <td class="button-cell text-end" v-if="isAddRequested">
            <button type="button" class="btn table-btn btn-outline-success" @click="save()">Add</button>
            <button type="button" class="btn table-btn btn-outline-secondary" @click="cancelSaveNewDiagnosis()">
                Cancel
            </button>
        </td>
    </tr>
</template>

<script lang="ts" src="./PastDiagnosisRow.ts" />
